

import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { UserService } from '../../../app/services/user/user.service';
import { RoutePaths } from '../paths/routePaths';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.css']
})
export class SidebarComponent implements OnInit {

  userRights: string[] = [];
  filteredSidebar: any[] = [];

  showMenu = '';
  showSubMenu = '';

  constructor(
    private userService: UserService,
    private router: Router
  ) { }

  ngOnInit(): void {

    // 🔥 ROUTE-DRIVEN OPEN STATE
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.syncMenuWithRoute();
      }
    });

    this.userService.rights$?.subscribe(rights => {
      this.userRights = rights;
      this.filteredSidebar = this.filterMenu(this.arr_obj_sidebarList);

      // initial load
      this.syncMenuWithRoute();
    });
  }

  toggleMenu(menu: string) {
    this.showMenu = this.showMenu === menu ? '' : menu;
    this.showSubMenu = '';
  }

  toggleSubMenu(subMenu: string) {
    this.showSubMenu = this.showSubMenu === subMenu ? '' : subMenu;
  }

  // 🔑 THIS PREVENTS RESET AFTER ADD / EDIT
  syncMenuWithRoute() {
    const url = this.router.url;

    this.showMenu = '';
    this.showSubMenu = '';

    for (const parent of this.filteredSidebar) {
      for (const level1 of parent.subMenu || []) {
        for (const level2 of level1.subMenu || []) {
          if (this.match(url, level2.route)) {
            this.showMenu = parent.label;
            this.showSubMenu = level1.label;
            return;
          }
        }

        if (this.match(url, level1.route)) {
          this.showMenu = parent.label;
          return;
        }
      }

      if (this.match(url, parent.route)) {
        return;
      }
    }
  }

  // Add this inside the SidebarComponent class
  trackByLabel(index: number, item: any): string {
    return item.label; // track by label
  }

  private match(url: string, route?: any[]): boolean {
    if (!route?.length) return false;
    return url.includes(route[route.length - 1]);
  }

  private hasRight(requiredRights?: string[]): boolean {
    if (!requiredRights?.length) return false;
    return requiredRights.some(r => this.userRights.includes(r));
  }

  private filterMenu(menuList: any[]): any[] {
    return menuList
      .map(menu => {
        const children = menu.subMenu?.length
          ? this.filterMenu(menu.subMenu)
          : [];

        const canShow =
          menu.alwaysShow ||
          this.hasRight(menu.rights) ||
          children.length > 0;

        return canShow ? { ...menu, subMenu: children } : null;
      })
      .filter(Boolean);
  }


  arr_obj_sidebarList: any[] = [

    // ---------------------------
    // HOME
    // ---------------------------
    {
      label: 'Home',
      route: ['/', RoutePaths.COMMON.HOME],
      icon: 'mdi mdi-home',
      subMenu: [],
      alwaysShow: true  // 👈 add this
    },


    // ---------------------------
    // MASTER MODULE
    // ---------------------------
    {
      label: 'Master',
      icon: 'mdi mdi-database',
      subMenu: [

        // DEPARTMENT
        {
          label: 'Department',
          icon: 'fa fa-sitemap',
          subMenu: [
            {
              label: 'Add',
              rights: ['Add Department'],
              icon: 'fa fa-plus-circle',
              route: ['/', RoutePaths.COMMON.MASTER, RoutePaths.DEPARTMENT.LIST, RoutePaths.DEPARTMENT.ADD]
            },
            {
              label: 'Edit',
              rights: ['Edit Department'],
              icon: 'fa fa-pencil',
              route: ['/', RoutePaths.COMMON.MASTER, RoutePaths.DEPARTMENT.LIST, RoutePaths.DEPARTMENT.EDIT]
            }
          ]
        },

        // ROLE
        {
          label: 'Role',
          icon: 'fa fa-user',
          subMenu: [
            {
              label: 'Add',
              rights: ['Add Role'],
              icon: 'fa fa-plus-circle',
              route: ['/', RoutePaths.COMMON.MASTER, RoutePaths.ROLE.LIST, RoutePaths.ROLE.ADD]
            },
            {
              label: 'Edit',
              rights: ['Edit Role'],
              icon: 'fa fa-pencil',
              route: ['/', RoutePaths.COMMON.MASTER, RoutePaths.ROLE.LIST, RoutePaths.ROLE.EDIT]
            }
          ]
        },


        // DESIGNATION
        {
          label: 'Designation',
          icon: 'fa fa-address-card',
          subMenu: [
            {
              label: 'Add',
              rights: ['Add Designation'],
              icon: 'fa fa-plus-circle',
              route: ['/', RoutePaths.COMMON.MASTER, RoutePaths.DESIGNATION.LIST, RoutePaths.DESIGNATION.ADD]
            },
            {
              label: 'Edit',
              rights: ['Edit Designation'],
              icon: 'fa fa-pencil',
              route: ['/', RoutePaths.COMMON.MASTER, RoutePaths.DESIGNATION.LIST, RoutePaths.DESIGNATION.EDIT]
            }
          ]
        },

        // USER
        {
          label: 'User',
          icon: 'fa fa-users',
          subMenu: [
            {
              label: 'Add',
              rights: ['Add User'],
              icon: 'fa fa-plus-circle',
              route: ['/', RoutePaths.COMMON.MASTER, RoutePaths.USER.LIST, RoutePaths.USER.ADD]
            },
            {
              label: 'Manage',
              rights: ['Manage User'],
              icon: 'fa fa-pencil',
              route: ['/', RoutePaths.COMMON.MASTER, RoutePaths.USER.LIST, RoutePaths.USER.MANAGE]
            }
          ]
        },

        // TEST TYPE
        { //testype = discipline both are same just inteernal code it will work as testype only
          label: 'Discipline',
          icon: 'fa fa-flask',
          subMenu: [
            {
              label: 'Add',
              rights: ['Add Type of Test'],
              icon: 'fa fa-plus-circle',
              route: ['/', RoutePaths.COMMON.MASTER, RoutePaths.INSPECTION.TESTTYPE, RoutePaths.INSPECTION.ADD_TESTTYPE]
            },
            {
              label: 'Edit',
              rights: ['Edit Type of Test'],
              icon: 'fa fa-pencil',
              route: ['/', RoutePaths.COMMON.MASTER, RoutePaths.INSPECTION.TESTTYPE, RoutePaths.INSPECTION.EDIT_TESTTYPE]
            }
          ]
        },

        // CATEGORY
        {
          label: 'Category',
          icon: 'mdi mdi-basket-fill',
          subMenu: [
            {
              label: 'Add',
              rights: ['Add Category'],
              icon: 'fa fa-plus-circle',
              route: ['/', RoutePaths.COMMON.MASTER, RoutePaths.CATEGORY.LIST, RoutePaths.CATEGORY.ADD]
            },
            {
              label: 'View',
              rights: ['View Category'],
              icon: 'fa fa-pencil',
              route: ['/', RoutePaths.COMMON.MASTER, RoutePaths.CATEGORY.LIST, RoutePaths.CATEGORY.VIEW]
            }
          ]
        },

        // INSPECTION CHECKLIST
        {
          label: 'Inspection Checklist',
          icon: 'mdi mdi-clipboard-outline',
          subMenu: [
            {
              label: 'Add',
              rights: ['Add Inspection CheckList'],
              icon: 'fa fa-plus-circle',
              route: ['/', RoutePaths.COMMON.MASTER, RoutePaths.INSPECTION.LIST, RoutePaths.INSPECTION.ADD]
            },
          ]
        }

      ]
    },

    // ---------------------------
    // CUSTOMER DETAIL
    // ---------------------------
    {
      label: 'Customer Registration',
      icon: 'fa fa-vcard-o',
      subMenu: [
        {
          label: 'Add',
          rights: ['Add Customer'],
          icon: 'fa fa-plus-circle',
          route: ['/', RoutePaths.COMMON.CUSTOMET_DETAIL, RoutePaths.CUTOMERDETAIL.LIST, RoutePaths.CUTOMERDETAIL.ADD]
        },
        {
          label: 'Manage',
          rights: ['Manage Customer'],
          icon: 'fa fa-pencil',
          route: ['/', RoutePaths.COMMON.CUSTOMET_DETAIL, RoutePaths.CUTOMERDETAIL.LIST, RoutePaths.CUTOMERDETAIL.MANAGE]
        }
      ]
    },
    // ---------------------------
    // New Enquiry
    // ---------------------------
    {
      label: 'New Enquiry',
      icon: 'mdi mdi-comment-account-outline',
      subMenu: [
        {
          label: 'Add',
          rights: ['Add New Enquiry'],
          icon: 'fa fa-plus-circle',
          route: ['/', RoutePaths.COMMON.NEW_ENQUIRY, RoutePaths.NEWENQUIRY.LIST, RoutePaths.NEWENQUIRY.ADD]
        },
        {
          label: 'Manage',
          rights: ['Manage Enquiry'],
          icon: 'fa fa-pencil',
          route: ['/', RoutePaths.COMMON.NEW_ENQUIRY, RoutePaths.NEWENQUIRY.LIST, RoutePaths.NEWENQUIRY.VIEW]
        },
      ]
    },

    // ---------------------------
    // CRF
    // ---------------------------
    {
      label: 'CRF',
      icon: 'mdi mdi-account-edit',
      subMenu: [
        {
          label: 'Manage',
          rights: ['CRF Approval 1'],
          icon: 'fa fa-pencil',
          route: ['/', RoutePaths.COMMON.CRF, RoutePaths.CRF.LIST, RoutePaths.CRF.MANAGE]
        }
      ]
    },

    // ---------------------------
    // Sample Verification
    // ---------------------------
    {
      label: 'Sample Verification',
      icon: 'fa fa-check-square-o',
      subMenu: [
        {
          label: 'Verification',
          rights: ['Sample Approval 1'],
          icon: 'fa fa-check',
          route: ['/', RoutePaths.COMMON.SAMPLE_VERIFICATION, RoutePaths.SAMPLEVERIFICATION.LIST, RoutePaths.SAMPLEVERIFICATION.ADD]
        },
        {
          label: 'Sample List',
          rights: ['Sample Approval 1','Sample Approval 2'],
          icon: 'fa fa-list',
          route: ['/', RoutePaths.COMMON.SAMPLE_VERIFICATION, RoutePaths.SAMPLEVERIFICATION.LIST, RoutePaths.SAMPLEVERIFICATION.VIEW]
        },
      ]
    },
    {
      label: 'Task List',
      rights: ['Lab Associate'],
      icon: 'mdi mdi-calendar-text',
      route: ['/', RoutePaths.COMMON.REPORT, RoutePaths.REPORTS.TASKLIST],
      subMenu: []
    },

    // ---------------------------
    // REPORT
    // ---------------------------
    {
      label: 'Report',
      icon: 'mdi mdi-clipboard-text',
      subMenu: [
        {
          label: 'Test Result',
          rights: [
            'Lab Associate',
            'Approve Initial Report',
            'Approve Final Report',
            'Generate Report',
            'Report Amendment',
            'Report Correction'
          ],
          icon: 'mdi mdi-file-find',
          route: ['/', RoutePaths.COMMON.REPORT, RoutePaths.REPORTS.TESTRESULT]
        },
        {
          label: 'Manage Report',
          rights: [
            'Approve Initial Report',
            'Approve Final Report',
            'Generate Report',
            'Report Amendment',
            'Report Correction'
          ],
          icon: 'mdi mdi-content-paste',
          route: ['/', RoutePaths.COMMON.REPORT, RoutePaths.REPORTS.TEST]
        },
        {
          label: 'User Status',
          icon: 'fa fa-users',
          route: ['/', RoutePaths.COMMON.REPORT, RoutePaths.REPORTS.USER_STATUS]
        },
        {
          label: 'Role Log',
          icon: 'fa fa-user',
          route: ['/', RoutePaths.COMMON.REPORT, RoutePaths.REPORTS.ROLE_LOG]
        }
      ]
    },

    // ---------------------------
    // AUDIT TRAIL
    // ---------------------------
    {
      label: 'Audit Trail',
      icon: 'mdi mdi-calendar-clock',
      subMenu: [
        {
          label: 'Department',
          rights: ['View Audit Trail'],
          icon: 'fa fa-sitemap',
          route: ['/', RoutePaths.COMMON.AUDIT_TRAIL, RoutePaths.AUDIT.DEPARTMENT]
        },
        {
          label: 'Role',
          rights: ['View Audit Trail'],
          icon: 'fa fa-users',
          route: ['/', RoutePaths.COMMON.AUDIT_TRAIL, RoutePaths.AUDIT.ROLE]
        }, {
          label: 'Designation',
          rights: ['View Audit Trail'],
          icon: 'fa fa-address-card',
          route: ['/', RoutePaths.COMMON.AUDIT_TRAIL, RoutePaths.AUDIT.DESIGNATION]
        },
        {
          label: 'User',
          rights: ['View Audit Trail'],
          icon: 'fa fa-user',
          route: ['/', RoutePaths.COMMON.AUDIT_TRAIL, RoutePaths.AUDIT.USER]
        },
        {
          label: 'Discipline',
          rights: ['View Audit Trail'],
          icon: 'fa fa-flask',
          route: ['/', RoutePaths.COMMON.AUDIT_TRAIL, RoutePaths.AUDIT.TESTTYPE]
        },
        {
          label: 'Enquiry',
          rights: ['View Audit Trail'],
          icon: 'mdi mdi-comment-account-outline',
          route: ['/', RoutePaths.COMMON.AUDIT_TRAIL, RoutePaths.AUDIT.ENQUIRY]
        },
        {
          label: 'Customer Registration',
          rights: ['View Audit Trail'],
          icon: 'fa fa-vcard-o',
          route: ['/', RoutePaths.COMMON.AUDIT_TRAIL, RoutePaths.AUDIT.CUSTOMER]
        },
        ,
        {
          label: 'CRF',
          rights: ['View Audit Trail'],
          icon: 'mdi mdi-account-edit',
          route: ['/', RoutePaths.COMMON.AUDIT_TRAIL, RoutePaths.AUDIT.CRF]
        }
        // {
        //   label: 'Category',
        //   icon: 'mdi mdi-basket-fill',
        //   route: ['/', RoutePaths.COMMON.AUDIT_TRAIL, RoutePaths.AUDIT.CATEGORY]
        // }
      ]
    },

    // ---------------------------
    // ACTIVITY LOG (direct)
    // ---------------------------
    {
      label: 'Activity Log',
      rights: ['Activity Log'],
      icon: 'fa fa-clock-o',
      route: ['/', RoutePaths.COMMON.AUDIT_TRAIL, RoutePaths.AUDIT.ACTIVITY_LOG],
      subMenu: []
    },
    {
      label: 'Customer Feedback',
      icon: 'mdi mdi-comment-account',
      route: ['/', RoutePaths.COMMON.AUDIT_TRAIL, RoutePaths.AUDIT.CUSTOMERFEEDBACK],
      subMenu: []
    }

  ];
}
